import { useBrowserEffect } from '@mnd-frontend/hooks';
import { type ReactNode, createContext, useContext, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { UiLibModal } from '../../UiLibModal';
import { useTranslation } from '../../contexts/UIContext';
import {
  NEUTRAL_WHITE,
  RADIUS_LG,
  SPACING_0_5,
  SPACING_2,
  SPACING_3,
  SPACING_6,
} from '../../theme';
import { Button } from '../Button';
import Icons from '../Icons';
import { panelLoadingCss } from '../Panel';
import { Text } from '../Text';
import media from '../utils/media';

const CloseButtonWrapper = styled.div`
  position: absolute;
  line-height: 0;
  z-index: 1;
  top: ${SPACING_3};
  right: ${SPACING_3};
`;

const BoxStyles = styled.div<{
  $size?: ModalProps['size'];
  $maxHeight?: string;
  $fixedHeight?: boolean;
  $openedAsModal?: boolean;
  $loading?: boolean;
}>`
  background-color: ${NEUTRAL_WHITE};
  --current-background: ${NEUTRAL_WHITE};
  border-radius: ${RADIUS_LG};
  display: flex;
  flex-direction: column;
  height: ${({ $fixedHeight }) => ($fixedHeight ? 'var(--card-max-height)' : '100%')};
  width:  100%;
  position: relative;
  ${({ $openedAsModal, $maxHeight, $size }) =>
    $openedAsModal &&
    css`
       --card-max-height: ${$maxHeight ?? '75vh'};
    max-height: var(--card-max-height);
    max-width: 95vw;
    width: ${
      $size === 'sm' ? '35rem' : $size === 'md' ? '50rem' : $size === 'lg' ? '64rem' : '100%'
    };
    
    ${media.lessThan('phablet')} {
      --card-max-height: 95vh;
    }
  `}
  ${({ $loading }) =>
    $loading &&
    css`
      ${panelLoadingCss}
      &:after {
        border-radius: ${RADIUS_LG};
      }
    `}
`;

const BoxForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Body = styled.div<{ $padding?: boolean; $openedAsModal?: boolean; $columnGap?: boolean }>`
  padding: ${({ $padding }) => ($padding ? SPACING_3 : '0')};
  flex: 1;
  ${({ $openedAsModal }) =>
    $openedAsModal &&
    css`
    scrollbar-width: thin;
    overflow-y: auto;
`}
  ${({ $columnGap }) =>
    $columnGap &&
    css`
      display: flex;
      flex-direction: column;
      gap: ${SPACING_3};
    `}
  &::-webkit-scrollbar {
    width: 0.5rem;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorderDividerDefault};
    border-radius: 1rem;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const Header = styled.header<{ $bottomBorder: boolean }>`
  padding: ${SPACING_3} ${SPACING_6} ${props => (props.$bottomBorder ? SPACING_3 : 0)} ${SPACING_3};
  display: flex;
  flex-direction: column;
  gap: ${SPACING_0_5};
  &:has(+ ${Body}) {
    border-bottom: ${({ $bottomBorder, theme }) => $bottomBorder && `1px solid ${theme.colorBorderDividerDefault}`};
  }
`;

const Footer = styled.footer`
  border-top: 1px solid ${({ theme }) => theme.colorBorderDividerDefault};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACING_3};
  padding: ${SPACING_2} ${SPACING_3};
`;

interface ModalProps {
  onClose?: () => void;
  size: 'sm' | 'md' | 'lg';
  openedAsModal: boolean;
  backdropOpacity?: number;
  headerBottomBorder?: boolean;
  bodyMinHeight?: string;
  maxHeight?: string;
  fixedHeight?: boolean;
  heightLimit?: string;
}

interface NonModalProps {
  size?: undefined;
  openedAsModal?: undefined;
  onClose?: undefined;
  backdropOpacity?: undefined;
  headerBottomBorder?: undefined;
  bodyMinHeight?: undefined;
  maxHeight?: undefined;
  fixedHeight?: undefined;
  heightLimit?: undefined;
}

interface RawProps {
  withHeaderAndFooter: false;
  headerContent?: undefined;
  footerContent?: undefined;
  description?: undefined;
  headline?: undefined;
}

interface NormalProps {
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  withHeaderAndFooter?: undefined | true;
  description?: ReactNode;
  headline?: ReactNode;
}

export type BoxProps = {
  id?: string;
  loading?: boolean;
  heightLimit?: string;
  children?: ReactNode;
  bodyPadding?: boolean;
  layout?: 'column-gap';
  // Will render the card content wrapped in a form with this submit handler
  onSubmit?: () => void;
} & (ModalProps | NonModalProps) &
  (RawProps | NormalProps);

const BoxContext = createContext<
  Pick<BoxProps, 'size'> & {
    footerAndHeaderHeight: number;
    setFooterHeight: (height: number) => void;
    setHeaderHeight: (height: number) => void;
    setHoistedFooterContent: (n: ReactNode) => void;
  }
>({
  size: undefined,
  footerAndHeaderHeight: 0,
  setHoistedFooterContent: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  setFooterHeight: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  setHeaderHeight: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
});

export const BoxHeader = ({
  children,
  headline,
  description,
  bottomBorder = true,
}: {
  children: ReactNode;
  description?: ReactNode;
  headline?: ReactNode;
  bottomBorder?: boolean;
}) => {
  const { size, setHeaderHeight } = useContext(BoxContext);
  const ref = useRef<HTMLDivElement>(null);

  useBrowserEffect(() => {
    if (!ref.current) return;

    const handleResize = () => {
      setHeaderHeight(ref.current?.offsetHeight ?? 0);
    };

    const resizeObserver = new window.ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(ref.current);
    handleResize();
    return () => resizeObserver.disconnect();
  }, [setHeaderHeight]);

  return (
    <Header ref={ref} $bottomBorder={bottomBorder}>
      {headline && (
        <Text as="h2" variant="headlineSmall" color="GREY_1000">
          {headline}
        </Text>
      )}
      {description && (
        <Text as="p" variant="bodySmall" color="GREY_800">
          {description}
        </Text>
      )}
      {children}
    </Header>
  );
};

export const BoxFooter = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { setFooterHeight, setHoistedFooterContent } = useContext(BoxContext);

  const [contentHoisted, setContentHoisted] = useState(false);

  useBrowserEffect(() => {
    if (!ref.current) return;

    const renderedInBody = ref.current.closest(Body.toString());
    if (renderedInBody) {
      setHoistedFooterContent(children);
      setContentHoisted(true);
    }
  }, [setHoistedFooterContent, children]);

  useBrowserEffect(() => {
    if (!ref.current) return;

    const handleResize = () => {
      setFooterHeight(ref.current?.offsetHeight ?? 0);
    };

    const resizeObserver = new window.ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(ref.current);
    handleResize();
    return () => resizeObserver.disconnect();
  }, [setFooterHeight]);

  return contentHoisted ? null : <Footer ref={ref}>{children}</Footer>;
};

export const BoxBody = ({
  children,
  padding = true,
  minHeight,
  openedAsModal,
  layout,
}: {
  children: ReactNode;
  padding?: boolean;
  minHeight?: string;
  openedAsModal?: boolean;
  layout?: 'column-gap';
}) => {
  const { footerAndHeaderHeight } = useContext(BoxContext);

  // If minHeight is set, we want to make sure that the card does not exceed the max height
  minHeight = minHeight
    ? `min(${minHeight}, calc(var(--card-max-height) - ${footerAndHeaderHeight}px))`
    : undefined;

  return (
    <Body
      $padding={padding}
      style={{ minHeight }}
      $openedAsModal={openedAsModal}
      $columnGap={layout === 'column-gap'}
    >
      {children}
    </Body>
  );
};

export const Box = ({
  openedAsModal,
  onClose,
  size,
  headerContent,
  footerContent,
  headline,
  children,
  headerBottomBorder,
  bodyPadding,
  bodyMinHeight,
  description,
  withHeaderAndFooter = true,
  onSubmit,
  maxHeight = '75vh',
  fixedHeight,
  backdropOpacity,
  loading,
  layout,
  ...forwardProps
}: BoxProps) => {
  const { t } = useTranslation();
  const [footerHeight, setFooterHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [hoistedFooterContent, setHoistedFooterContent] = useState<ReactNode>(null);

  footerContent ||= hoistedFooterContent;

  const cardChildren = withHeaderAndFooter ? (
    <>
      {headline || description || headerContent ? (
        <BoxHeader headline={headline} description={description} bottomBorder={headerBottomBorder}>
          {headerContent}
        </BoxHeader>
      ) : null}
      {children && (
        <BoxBody
          minHeight={bodyMinHeight}
          padding={bodyPadding}
          openedAsModal={openedAsModal}
          layout={layout}
        >
          {children}
        </BoxBody>
      )}
      {footerContent && <BoxFooter>{footerContent}</BoxFooter>}
    </>
  ) : (
    children
  );
  const potentiallyWrappedInForm = onSubmit ? (
    <BoxForm
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {cardChildren}
    </BoxForm>
  ) : (
    cardChildren
  );

  return (
    <BoxContext.Provider
      value={useMemo(
        () => ({
          size,
          setFooterHeight,
          setHeaderHeight,
          footerAndHeaderHeight: footerHeight + headerHeight,
          setHoistedFooterContent,
        }),
        [size, setFooterHeight, setHeaderHeight, footerHeight, headerHeight],
      )}
    >
      {typeof openedAsModal === 'boolean' ? (
        <UiLibModal open={openedAsModal} backdropOpacity={backdropOpacity}>
          <BoxStyles
            {...forwardProps}
            $size={size}
            $maxHeight={maxHeight}
            $fixedHeight={fixedHeight}
            $openedAsModal
            $loading={loading}
          >
            {onClose && (
              <CloseButtonWrapper>
                <Button
                  inline
                  configuration="text"
                  type="button"
                  aria-label={t('common_close')}
                  icon={<Icons.Close />}
                  onClick={onClose}
                />
              </CloseButtonWrapper>
            )}
            {potentiallyWrappedInForm}
          </BoxStyles>
        </UiLibModal>
      ) : (
        <BoxStyles {...forwardProps} $loading={loading}>
          {potentiallyWrappedInForm}
        </BoxStyles>
      )}
    </BoxContext.Provider>
  );
};
